'use client';

import { FC, useEffect, useRef, useState } from 'react';
import { Tooltip } from '../components';
import OrganogenesisCorpLogo from '../../../../public/images/qm2lumqgpeqaui9lbs82.webp';
import OrganogenesisCorpLogoWhite from '../../../../public/images/jtq7ncm3vjjtqqn3uiw0.webp';
import ExportedImage from 'next-image-export-optimizer';
import { Button } from '../button/button';
import { AppRoute } from '../../enums/app-route.enum';
import { AsideNavigatorNavList } from './libs/components/aside-navigator-nav-list/aside-navigator-nav-list';
import { usePathname } from 'next/navigation';
import { cn } from '@shared/shared-utils';
import { ValueOf } from '../../types/types';
import ArrowRightIcon from '../../../../public/svg/arrow-right.svg';
import Link from 'next/link';

const AsideNavigator: FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const asideRef = useRef<HTMLElement | null>(null);
  const [isAsideOutside, setIsAsideOutside] = useState(false);

  const pathname = usePathname();

  const isMainPage = pathname === AppRoute.MAIN;
  const isFindAProfessionalPage = pathname === AppRoute.FIND_A_PROFESSIONAL;

  useEffect(() => {
    const containerEl = containerRef.current;
    const asideEl = asideRef.current;

    if (!containerEl || !asideEl) {
      return;
    }

    const checkOverflow = () => {
      const containerRect = containerEl.getBoundingClientRect();
      const asideRect = asideEl.getBoundingClientRect();

      const isOutsideVertically = asideRect.bottom > containerRect.bottom;

      const isOutside = isOutsideVertically;

      if (isAsideOutside !== isOutside) {
        setIsAsideOutside(isOutside);
      }
    };

    checkOverflow();

    window.addEventListener('scroll', checkOverflow, { passive: true });
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('scroll', checkOverflow);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [isAsideOutside]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'row-start-1 col-start-1 h-full hidden relative z-10 lg:block',
        {
          'bg-[linear-gradient(90deg,_#041C2C_12.8%,_rgba(4,28,44,0.72)_42.05%,_rgba(0,40,85,_0.00)_91%)]':
            isMainPage,
          'bg-ogn-white': !isMainPage,
          'lg:hidden 2xl:block': isMainPage,
        },
      )}
    >
      <aside
        ref={asideRef}
        className={cn(
          'fixed z-40 h-min w-full max-w-[360px] top-[68px] py-12 bg-transparent text-ogn-blue',
          {
            'bg-black bg-opacity-35 text-ogn-white': isAsideOutside,
            'top-0': isFindAProfessionalPage,
          },
        )}
        aria-label="Site navigation"
      >
        <Link href={AppRoute.MAIN} aria-label="Go to home">
          <div
            className={cn(
              'ml-12 w-[200px] pb-[30px] border-b-[1px] border-ogn-blue',
              {
                'border-b-ogn-white': isMainPage || isAsideOutside,
              },
            )}
          >
            <ExportedImage
              src={
                isMainPage || isAsideOutside
                  ? OrganogenesisCorpLogoWhite
                  : OrganogenesisCorpLogo
              }
              alt="Organogenesis Corp Logo"
              width={68}
              height={94}
              className="mx-auto"
            />
          </div>
        </Link>
        <AsideNavigatorNavList
          pathname={pathname as ValueOf<typeof AppRoute>}
        />
        <Tooltip className="ml-12 mt-10" content="Get updates">
          <Button
            to={AppRoute.GET_UPDATES}
            variant="blue"
            className={cn(
              'flex items-center gap-x-2 rounded-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-ogn-bright-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
              {
                'bg-black bg-opacity-50 border border-ogn-white': isMainPage,
              },
            )}
            aria-label="Get updates"
          >
            Get Updates{' '}
            <ArrowRightIcon
              className="w-[16px] h-[16px]"
              aria-hidden="true"
              focusable="false"
            />
          </Button>
        </Tooltip>
      </aside>
    </div>
  );
};

export { AsideNavigator };
